import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePaddle } from '../../contexts/PaddleContext';
import axiosInstance from '../../actions/utils/axiosInstance';
import StripyBackground from '../login/DiagonalCanvas';
import TrialStatusBanner from '../../components/subscription/TrialStatusBanner';

interface TierInfo {
  id: string;
  name: string;
  priceId: string;
  price?: string;
  interval?: string;
  features: string[];
}

interface PricingData {
  paddleEnvironment: string;
  tiers: TierInfo[];
}

const PageContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  backgroundColor: 'transparent',
  marginTop: '64px',
  minHeight: 'calc(100vh - 64px)',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}));

const CHECKOUT_ERROR_MESSAGE =
  "We couldn't start your checkout. Please try again, or contact support if it keeps happening.";

const PricingPage: React.FC = () => {
  const { user, refreshStatus, trialState } = useAuth();
  const { paddle, isReady, onCheckoutComplete, offCheckoutComplete } = usePaddle();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutInProgress, setCheckoutInProgress] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    axiosInstance
      .get<{ success: boolean; data: PricingData }>('/subscription/pricing')
      .then((res) => {
        if (res.data?.data?.tiers?.length) {
          setTiers(res.data.data.tiers);
        } else {
          setError("Pricing isn't available right now. Please try again in a moment or contact support.");
        }
      })
      .catch(() => {
        setError("Pricing isn't available right now. Please try again in a moment or contact support.");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleCheckoutCompleted = useCallback(async () => {
    setCheckoutInProgress(false);
    await refreshStatus();
    window.location.href = '/?subscribed=1';
  }, [refreshStatus]);

  useEffect(() => {
    onCheckoutComplete(handleCheckoutCompleted);
    return () => offCheckoutComplete(handleCheckoutCompleted);
  }, [handleCheckoutCompleted, onCheckoutComplete, offCheckoutComplete]);

  const handleSubscribe = async (priceId: string) => {
    if (!paddle || !priceId || !user || checkoutInProgress) return;

    setCheckoutInProgress(true);
    setCheckoutError(null);

    let transactionId: string | undefined;
    try {
      const res = await axiosInstance.post<{ data?: { transactionId?: string } }>(
        '/subscription/checkout',
        { priceId },
      );
      transactionId = res.data?.data?.transactionId;
    } catch {
      transactionId = undefined;
    }

    if (!transactionId) {
      setCheckoutError(CHECKOUT_ERROR_MESSAGE);
      setCheckoutInProgress(false);
      return;
    }

    try {
      paddle.Checkout.open({
        transactionId,
        settings: {
          theme: 'dark',
          successUrl: window.location.origin + '/?subscribed=1',
        },
      });
    } catch {
      setCheckoutError(CHECKOUT_ERROR_MESSAGE);
    }
    // The Paddle overlay is modal and we get no close event for a dismissed checkout, so release
    // the lock once it has been handed over; a completed checkout navigates away.
    setCheckoutInProgress(false);
  };

  return (
    <>
      <StripyBackground />
      <CssBaseline />
      <PageContainer>
        <Box sx={{ maxWidth: 900, width: '100%', textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}
          >
            Choose Your Plan
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Get started with AI Scribe. Cancel anytime.
          </Typography>

          {trialState && (trialState.status === 'trialing' || trialState.status === 'trial_expired') && (
            <TrialStatusBanner trial={trialState} />
          )}

          {trialState?.status === 'trialing' && !trialState.blocked && (
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/')}
                data-testid="continue-with-trial-cta"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                Continue with free trial
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                You can subscribe anytime — no card needed during the trial.
              </Typography>
            </Box>
          )}

          {loading ? (
            <CircularProgress sx={{ mt: 4 }} data-testid="pricing-loading" />
          ) : error ? (
            <Alert severity="error" sx={{ mt: 4, textAlign: 'left' }} data-testid="pricing-error">
              {error}
            </Alert>
          ) : (
            <>
              {checkoutError && (
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }} data-testid="checkout-error">
                  {checkoutError}
                </Alert>
              )}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                justifyContent="center"
                alignItems="stretch"
                data-testid="pricing-tiers"
              >
                {tiers.map((tier, idx) => {
                  const isPro = tier.id === 'pro';
                  return (
                    <Card
                      key={tier.id}
                      sx={{
                        flex: 1,
                        maxWidth: 400,
                        position: 'relative',
                        border: isPro ? '2px solid' : '1px solid',
                        borderColor: isPro ? 'primary.main' : 'divider',
                        borderRadius: '12px',
                        overflow: 'visible',
                      }}
                    >
                      {isPro && (
                        <Chip
                          label="Most Popular"
                          color="primary"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontWeight: 600,
                          }}
                        />
                      )}
                      <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                          {tier.name}
                        </Typography>

                        {tier.price && (
                          <Box sx={{ mb: 2 }}>
                            <Typography component="span" variant="h4" sx={{ fontWeight: 800 }}>
                              {tier.price}
                            </Typography>
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}/ {tier.interval || 'month'}
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ flex: 1, textAlign: 'left', mb: 3 }}>
                          {tier.features.map((feature) => (
                            <Stack
                              key={feature}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <CheckIcon fontSize="small" color="success" />
                              <Typography variant="body2">{feature}</Typography>
                            </Stack>
                          ))}
                        </Box>

                        <Button
                          variant={isPro ? 'contained' : 'outlined'}
                          size="large"
                          fullWidth
                          disabled={!isReady || !tier.priceId || checkoutInProgress}
                          onClick={() => handleSubscribe(tier.priceId)}
                        >
                          {checkoutInProgress ? (
                            <CircularProgress size={22} color="inherit" />
                          ) : (
                            `Subscribe to ${tier.name}`
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}

          <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary' }}>
            Secure payment powered by Paddle. All prices include applicable taxes.
          </Typography>
        </Box>
      </PageContainer>
    </>
  );
};

export default PricingPage;
